import { isEmpty, merge } from 'lodash-es';

import { interactionsFor } from './axis-interactions.js';
import { doesNothing } from './defaults.js';
import { isXAxis } from './is-axis.js';
import type { TruncationMode } from '../../../typography/text-ellipsis/TextEllipsis.js';
import { TRUNCATION_MODE } from '../../core/constants.js';
import { isValidColorRule } from '../../core/slots/ColorRule.js';
import { isCustomColorPalette } from '../../core/types/color-palette.js';
import { isColoredRangeArray } from '../../core/types/colored-range.js';
import { ChartTooltipConfigProps } from '../../core/types/tooltip.js';
import { isValidColorPalette } from '../../core/utils/is-valid-color-palette.js';
import { isValidLegendPosition } from '../../core/utils/is-valid-legend-position.js';
import { isValidToolbarPlacement } from '../../core/utils/is-valid-toolbar-placement.js';
import { buildThresholds } from '../components/thresholds/builder.js';
import {
  DEFAULT_AXIS_CONFIG,
  defaultConfig,
  defaultLegend,
  defaultToolbar,
} from '../constants/defaults.js';
import { XYChartAnnotationsConfigProps } from '../context/XYChartAnnotationsConfig.context.js';
import {
  DEFAULT_CUSTOM_TOOLTIP_CONFIG,
  XYChartCustomTooltipConfig,
} from '../context/XYChartCustomTooltip.context.js';
import { XYAxisMapper } from '../types/axis.js';
import {
  type ExportableXYChartProps,
  type XYChartAxisInternal,
  type XYChartInternals,
  type XYChartSeriesBasePropsExtended,
  type XYChartSlots,
} from '../types/xy-chart-internals.js';
import type {
  XYChartAxisProps,
  XYChartChartLegendProps,
  XYChartConfigProperties,
  XYChartSelectConfig,
  XYChartToolbarConfigProps,
  XYChartToolbarSlotProps,
} from '../types/xy-chart-props.js';
import {
  NUMERICAL_SCALE_TYPE,
  type XYChartColorPalette,
} from '../types/xy-chart.js';

/**
 * Builds a legend configuration based on context and slots
 * @param slotsLegend - legend slots
 * @param contextLegend - legend context
 */
function getLegendConfiguration(
  slotsLegend: XYChartChartLegendProps,
  contextLegend: XYChartChartLegendProps | undefined,
): XYChartChartLegendProps {
  const legendConfig: XYChartChartLegendProps = merge(
    {},
    defaultLegend,
    contextLegend,
    slotsLegend,
  );

  legendConfig.position = isValidLegendPosition(legendConfig.position)
    ? legendConfig.position
    : defaultLegend.position;

  return legendConfig;
}

function getToolbarConfiguration(
  slotsToolbar: XYChartToolbarSlotProps,
  contextToolbar: XYChartToolbarConfigProps | undefined,
) {
  const toolbarConfig = merge({}, defaultToolbar, contextToolbar, slotsToolbar);

  toolbarConfig.placement = isValidToolbarPlacement(toolbarConfig.placement)
    ? toolbarConfig.placement
    : defaultToolbar.placement;

  return toolbarConfig;
}

function getAxesWithSeries(
  axisSlots: XYChartAxisInternal[],
  series: XYChartSeriesBasePropsExtended[],
) {
  let axesConfig: XYChartAxisInternal[] = [];
  const matchingIds = new Set();

  axisSlots.forEach((axis) => {
    const isAlreadyDefined = matchingIds.has(axis.id);

    if (isAlreadyDefined) {
      return;
    }

    const hasMatchingSeriesId = series.find((series) => {
      return isXAxis(axis)
        ? series.xAxisId === axis.id
        : series.yAxisId === axis.id;
    });

    if (hasMatchingSeriesId) {
      matchingIds.add(axis.id);
      axesConfig = [...axesConfig, axis];
    }
  });

  return axesConfig;
}

/**
 * Builds a ColorPalette configuration based on context and props
 * @param contextColorPalette - color palette context
 * @param propsColorPalette - color palette props
 */
function getColorPaletteConfiguration(
  contextColorPalette?: XYChartColorPalette,
  propsColorPalette?: XYChartColorPalette,
) {
  const colorPalette = propsColorPalette ?? contextColorPalette ?? undefined;

  if (!colorPalette) {
    return undefined;
  }

  return isColoredRangeArray(colorPalette) ||
    isCustomColorPalette(colorPalette) ||
    isValidColorPalette(colorPalette)
    ? colorPalette
    : undefined;
}

export function getTruncationModeConfiguration(
  contextTruncationMode?: TruncationMode,
  propsTruncationMode?: TruncationMode,
) {
  return propsTruncationMode ?? contextTruncationMode ?? TRUNCATION_MODE.Middle;
}

export function getXYChartAnnotationsConfiguration(
  slotsAnnotations?: XYChartAnnotationsConfigProps,
  configAnnotations?: XYChartAnnotationsConfigProps,
) {
  if (isEmpty(slotsAnnotations) && !configAnnotations) {
    return undefined;
  }
  return merge({}, configAnnotations, slotsAnnotations);
}

function getCustomTooltipConfiguration(
  slotsCustomTooltip: XYChartCustomTooltipConfig | undefined,
  contextCustomTooltip: ChartTooltipConfigProps | undefined,
): XYChartCustomTooltipConfig {
  if (slotsCustomTooltip !== undefined) {
    return slotsCustomTooltip;
  }
  return {
    ...DEFAULT_CUSTOM_TOOLTIP_CONFIG,
    hidden:
      contextCustomTooltip?.hidden ?? DEFAULT_CUSTOM_TOOLTIP_CONFIG.hidden,
  };
}

/**
 * Creates a XY chart configuration merging the configuration
 * provided in the context config, slots, and defaults.
 * @param slots - xy chart slots
 * @param contextConfig - config pass in config context
 * @param rootProps - Config props passed in root component level
 */
export type XYChartConfig = Omit<
  Required<XYChartConfigProperties>,
  'annotations' | 'select' | 'colorPalette' | 'tooltip'
> & {
  annotations?: XYChartAnnotationsConfigProps;
  select?: XYChartSelectConfig;
  colorPalette?: XYChartColorPalette;
  tooltip: XYChartCustomTooltipConfig;
};

export const createXYChartConfiguration = (
  slots: XYChartSlots,
  contextConfig: XYChartConfigProperties,
  rootProps: ExportableXYChartProps,
): XYChartConfig => {
  const colorPalette = getColorPaletteConfiguration(
    contextConfig.colorPalette,
    rootProps?.colorPalette,
  );

  const truncationMode = getTruncationModeConfiguration(
    contextConfig.truncationMode,
    rootProps?.truncationMode,
  );

  const legend = getLegendConfiguration(slots.legend, contextConfig.legend);
  const xAxes = getAxes(slots.xAxes, contextConfig.xAxes);
  const yAxes = getAxes(slots.yAxes, contextConfig.yAxes);
  const toolbar = getToolbarConfiguration(slots.toolbar, contextConfig.toolbar);
  const annotations = getXYChartAnnotationsConfiguration(
    slots.annotations,
    contextConfig.annotations,
  );
  const select = slots.select || undefined;
  const thresholdIndicators = [
    ...slots.thresholds,
    ...(contextConfig.thresholdIndicators || []),
  ];
  const colorRules = (contextConfig.colorRules || [])
    .filter(isValidColorRule)
    .concat(slots.colorRules || []);
  const tooltip = getCustomTooltipConfiguration(
    slots.tooltip,
    contextConfig.tooltip,
  );

  return {
    colorPalette,
    legend,
    toolbar,
    truncationMode,
    xAxes,
    yAxes,
    thresholdIndicators,
    annotations,
    select,
    colorRules,
    tooltip,
    customTooltip: {},
  };
};

export function getAxes<T extends XYChartAxisProps>(
  slotAxes: T[],
  contextAxes: T[] | undefined,
): T[] {
  const configContextAxes = contextAxes ?? [];
  const ids = new Set<string>();
  const axesConfig = [...slotAxes, ...configContextAxes];

  return axesConfig.reduce<T[]>((acc: T[], axis: T) => {
    if (!ids.has(axis.id)) {
      acc.push({ ...DEFAULT_AXIS_CONFIG, ...axis });
      ids.add(axis.id);
    }

    return acc;
  }, []);
}

export const deserializeConfig = (
  serializedConfig: string,
): XYChartConfigProperties => {
  try {
    return merge({}, defaultConfig, JSON.parse(serializedConfig));
  } catch {
    return defaultConfig;
  }
};

type InternalConfig = {
  xAxes: XYChartSlots['xAxes'];
  yAxes: XYChartSlots['yAxes'];
  thresholdIndicators: XYChartSlots['thresholds'];
};
type InternalSlots = {
  series: XYChartSlots['series'];
};
type BuildInternalConfig = (
  config: InternalConfig,
  slots: InternalSlots,
) => XYChartInternals;

export const buildInternalConfig: BuildInternalConfig = (config, slots) => {
  const yAxes = config.yAxes.map((axis) => ({
    ...axis,
    coordinate: 'y',
    tickLabelLayout: 'horizontal',
    type: XYAxisMapper[axis.type],
    interactions: { zoom: false, pan: false, infinite: false },
    onZoomChange: doesNothing,
    isRelative:
      axis.type === NUMERICAL_SCALE_TYPE &&
      axis.valueRepresentation === 'relative',
  }));

  const xAxes = config.xAxes.map((axis) => ({
    ...axis,
    coordinate: 'x' as const,
    tickLabelLayout: axis.tickLabelLayout ?? 'horizontal',
    type: XYAxisMapper[axis.type],
    interactions: interactionsFor(axis),
    onZoomChange: axis.onZoomChange ?? doesNothing,
    isRelative:
      axis.type === NUMERICAL_SCALE_TYPE &&
      axis.valueRepresentation === 'relative',
  }));

  const axes = getAxesWithSeries(
    [...yAxes, ...xAxes] as XYChartAxisInternal[],
    slots.series,
  );

  const thresholds = buildThresholds(yAxes, xAxes, config.thresholdIndicators);

  return { series: slots.series, axes, thresholds };
};
